import { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import {
  type WidgetFilterSettings,
  type WidgetDateRange,
  type WidgetMarket,
  getUniqueExchangesFromTrades,
} from '../lib/tradeCalculations'

interface Strategy {
  id: string
  display_name: string
}

interface WidgetAccount {
  id: string
  name: string
  platform: string
  currency: string
}

interface WidgetFilterPopoverProps {
  anchorRef: React.RefObject<HTMLElement | null>
  isOpen: boolean
  onClose: () => void
  settings: WidgetFilterSettings
  onSave: (settings: WidgetFilterSettings) => void
  strategies: Strategy[]
  trades: { exchange?: string | null; market?: string | null; tags?: string[] | null }[]
  accounts?: WidgetAccount[]
  hiddenFilters?: ('dateRange' | 'strategy' | 'account' | 'tag')[]
}

const MARKET_OPTIONS: { value: WidgetMarket; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'Stock', label: 'Stock' },
  { value: 'Forex', label: 'Forex' },
  { value: 'Crypto', label: 'Crypto' },
  { value: 'Commodity', label: 'Commodity' },
]

const DATE_RANGE_OPTIONS: { value: WidgetDateRange; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: '1d', label: '1D' },
  { value: '1w', label: '1W' },
  { value: '1m', label: '1M' },
  { value: '3m', label: '3M' },
  { value: '6m', label: '6M' },
  { value: 'ytd', label: 'YTD' },
  { value: '1y', label: '1Y' },
]

export default function WidgetFilterPopover({
  anchorRef,
  isOpen,
  onClose,
  settings,
  onSave,
  strategies,
  trades,
  accounts = [],
  hiddenFilters = [],
}: WidgetFilterPopoverProps) {
  const { t } = useTranslation('dashboard')
  const [dateRange, setDateRange] = useState<WidgetDateRange>(settings.dateRange)
  const [strategyId, setStrategyId] = useState(settings.strategyId)
  const [exchange, setExchange] = useState(settings.exchange)
  const [market, setMarket] = useState<WidgetMarket>(settings.market ?? 'all')
  const [accountId, setAccountId] = useState(settings.accountId ?? '')
  const [tag, setTag] = useState(settings.tag ?? '')
  const popoverRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setDateRange(settings.dateRange)
    setStrategyId(settings.strategyId)
    setExchange(settings.exchange)
    setMarket(settings.market ?? 'all')
    setAccountId(settings.accountId ?? '')
    setTag(settings.tag ?? '')
  }, [settings, isOpen])

  useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(e.target as Node)
      ) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, onClose, anchorRef])

  const exchanges = getUniqueExchangesFromTrades(trades)

  const uniqueTags = useMemo(() => {
    const tagSet = new Set<string>()
    for (const trade of trades) {
      for (const t of trade.tags ?? []) tagSet.add(t)
    }
    return [...tagSet].sort()
  }, [trades])

  const handleApply = () => {
    onSave({ dateRange, strategyId, exchange, market, accountId, tag })
    onClose()
  }

  const [position, setPosition] = useState({ top: 0, left: 0 })

  const updatePosition = useCallback(() => {
    if (!anchorRef.current) return
    const rect = anchorRef.current.getBoundingClientRect()
    setPosition({
      top: rect.bottom + 4,
      left: Math.min(rect.right - 224, window.innerWidth - 232),
    })
  }, [anchorRef])

  useLayoutEffect(() => {
    if (!isOpen || !anchorRef.current) return
    updatePosition()
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [isOpen, anchorRef, updatePosition])

  if (!isOpen) return null

  const popoverContent = (
    <div
      ref={popoverRef}
      className="fixed z-[9999] w-56 rounded-lg border border-gray-200 bg-white p-3 shadow-lg"
      style={{ top: position.top, left: position.left }}
    >
      <p className="mb-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
        {t('dashboard:editLayout.filterWidget')}
      </p>
      <div className="space-y-2">
        {!hiddenFilters.includes('dateRange') && (
          <div>
            <label className="mb-0.5 block text-xs text-gray-600">
              {t('dashboard:editLayout.dateRange')}
            </label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value as WidgetDateRange)}
              className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
            >
              {DATE_RANGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        )}
        {!hiddenFilters.includes('strategy') && (
          <div>
            <label className="mb-0.5 block text-xs text-gray-600">
              {t('editLayout.strategy')}
            </label>
            <select
              value={strategyId}
              onChange={(e) => setStrategyId(e.target.value)}
              className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
            >
              <option value="">All</option>
              {strategies.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.display_name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className="mb-0.5 block text-xs text-gray-600">
            {t('editLayout.market')}
          </label>
          <select
            value={market}
            onChange={(e) => setMarket(e.target.value as WidgetMarket)}
            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
          >
            {MARKET_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-0.5 block text-xs text-gray-600">
            {t('editLayout.exchange')}
          </label>
          <select
            value={exchange}
            onChange={(e) => setExchange(e.target.value)}
            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
          >
            <option value="all">All</option>
            {exchanges.map((ex) => (
              <option key={ex} value={ex}>
                {ex}
              </option>
            ))}
          </select>
        </div>
        {!hiddenFilters.includes('account') && (
          <div>
            <label className="mb-0.5 block text-xs text-gray-600">
              {t('editLayout.account', 'Account')}
            </label>
            <select
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
            >
              <option value="">All</option>
              {accounts.map((acc) => (
                <option key={acc.id} value={acc.id}>
                  {acc.name}
                </option>
              ))}
            </select>
          </div>
        )}
        {!hiddenFilters.includes('tag') && (
          <div>
            <label className="mb-0.5 block text-xs text-gray-600">
              {t('editLayout.tag', 'Tag')}
            </label>
            <select
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
            >
              <option value="">All</option>
              {uniqueTags.map((tg) => (
                <option key={tg} value={tg}>
                  {tg}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={handleApply}
        className="mt-3 w-full rounded bg-orange-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-600"
      >
        {t('editLayout.apply')}
      </button>
    </div>
  )

  return createPortal(popoverContent, document.body)
}
